/**
 * HYPSEYA — Apple Health / HRV Integration Hook
 * Package: @capgo/capacitor-health (v8, Capacitor 8 compatible)
 *
 * Reads from Apple HealthKit (iOS) / Health Connect (Android):
 * - heartRateVariability — key nervous system regulation biomarker
 * - restingHeartRate
 * - sleep (total hours last night)
 *
 * iOS setup (one-time, already partially done):
 *   1. npx cap sync ios  (run below — already done)
 *   2. Xcode → Signing & Capabilities → + Capability → HealthKit  ← do this once
 *   Info.plist already has NSHealthShareUsageDescription via existing config.
 */

import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';

// ── Plugin loader ──────────────────────────────────────────────────────────
async function getHealthPlugin() {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    const { Health } = await import('@capgo/capacitor-health');
    return Health;
  } catch {
    return null;
  }
}

// ── Main hook ─────────────────────────────────────────────────────────────
export function useHealthKit() {
  const [hrv,          setHrv]          = useState(null);   // ms (SDNN)
  const [restingHR,    setRestingHR]    = useState(null);   // bpm
  const [sleepHours,   setSleepHours]   = useState(null);   // hours
  const [isAvailable,  setIsAvailable]  = useState(false);
  const [isSimulated,  setIsSimulated]  = useState(false);
  const [error,        setError]        = useState(null);
  const [isLoading,    setIsLoading]    = useState(true);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setIsLoading(true);
    const Health = await getHealthPlugin();

    if (!Health) {
      // Browser / plugin not installed — return simulated data for UI dev
      setIsSimulated(true);
      setIsAvailable(false);
      setHrv(null);
      setRestingHR(null);
      setSleepHours(null);
      setIsLoading(false);
      return;
    }

    try {
      // Request read permissions for HRV, resting HR, sleep
      await Health.requestAuthorization({
        read:  ['heartRateVariability', 'restingHeartRate', 'sleep'],
        write: []
      });

      setIsAvailable(true);

      const endDate   = new Date();
      const startDate = new Date(endDate);
      startDate.setDate(startDate.getDate() - 7);

      // HRV — last 7 days, most recent value
      const hrvData = await Health.readSamples({
        dataType:  'heartRateVariability',
        startDate: startDate.toISOString(),
        endDate:   endDate.toISOString(),
        limit:     5
      }).catch(() => ({ samples: [] }));

      if (hrvData.samples?.length > 0) {
        const latest = hrvData.samples[hrvData.samples.length - 1];
        setHrv(Math.round(latest.value));
      }

      // Resting heart rate — last 7 days average
      const hrData = await Health.readSamples({
        dataType:  'restingHeartRate',
        startDate: startDate.toISOString(),
        endDate:   endDate.toISOString(),
        limit:     5
      }).catch(() => ({ samples: [] }));

      if (hrData.samples?.length > 0) {
        const avg = hrData.samples.reduce((a, b) => a + b.value, 0) / hrData.samples.length;
        setRestingHR(Math.round(avg));
      }

      // Sleep — last night (from 6pm yesterday)
      const sleepStart = new Date();
      sleepStart.setDate(sleepStart.getDate() - 1);
      sleepStart.setHours(18, 0, 0, 0);

      const sleepData = await Health.readSamples({
        dataType:  'sleep',
        startDate: sleepStart.toISOString(),
        endDate:   endDate.toISOString(),
        limit:     20
      }).catch(() => ({ samples: [] }));

      if (sleepData.samples?.length > 0) {
        const total = sleepData.samples
          .filter(d => ['asleep', 'rem', 'deep', 'light'].includes(d.sleepState))
          .reduce((acc, d) => {
            const s = new Date(d.startDate);
            const e = new Date(d.endDate);
            return acc + (e - s) / 3600000;
          }, 0);
        if (total > 0) setSleepHours(Math.round(total * 10) / 10);
      }

    } catch (err) {
      console.error('HealthKit error:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // HRV interpretation (clinical: SDNN)
  const hrvInterpretation = hrv === null ? null
    : hrv >= 60 ? { label: 'Good HRV', color: '#2D8B87', tone: 'Your nervous system shows healthy variability.' }
    : hrv >= 40 ? { label: 'Moderate HRV', color: '#4AACA8', tone: 'Moderate variability. Rest and practice support recovery.' }
    : { label: 'Low HRV', color: '#B8906A', tone: 'Lower variability suggests the nervous system may be under load.' };

  return {
    hrv,
    restingHR,
    sleepHours,
    isAvailable,
    isSimulated,
    isLoading,
    error,
    refresh: load,
    hrvInterpretation
  };
}

// ── HRV card component (used in Home + Window of Tolerance) ───────────────
export function HRVCard({ compact = false }) {
  const { hrv, restingHR, sleepHours, isAvailable, isSimulated, isLoading, hrvInterpretation } = useHealthKit();

  if (isLoading) return null;

  if (!isAvailable && !isSimulated) {
    return (
      <div style={{
        background: 'rgba(247,243,236,0.8)', borderRadius: '14px',
        padding: '14px 16px',
        border: '1px solid rgba(184,211,207,0.3)',
        fontSize: '0.78rem', color: '#8FA3A0', fontStyle: 'italic'
      }}>
        Apple Health not connected.{' '}
        <span style={{ color: '#2D8B87', fontStyle: 'normal', fontWeight: '600' }}>
          Enable in Settings → Health → HYPSEYA
        </span>
      </div>
    );
  }

  if (compact) {
    return (
      <div style={{
        display: 'flex', gap: '8px', flexWrap: 'wrap'
      }}>
        {hrv && (
          <div style={{
            background: 'rgba(255,255,255,0.9)', borderRadius: '10px',
            padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '6px'
          }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: hrvInterpretation?.color || '#B8D3CF' }} />
            <span style={{ fontSize: '0.72rem', color: '#8FA3A0', fontWeight: '600' }}>HRV</span>
            <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#1E3A39' }}>{hrv}ms</span>
          </div>
        )}
        {restingHR && (
          <div style={{
            background: 'rgba(255,255,255,0.9)', borderRadius: '10px',
            padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '6px'
          }}>
            <span style={{ fontSize: '0.72rem', color: '#8FA3A0', fontWeight: '600' }}>HR</span>
            <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#1E3A39' }}>{restingHR} bpm</span>
          </div>
        )}
        {sleepHours && (
          <div style={{
            background: 'rgba(255,255,255,0.9)', borderRadius: '10px',
            padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '6px'
          }}>
            <span style={{ fontSize: '0.72rem', color: '#8FA3A0', fontWeight: '600' }}>Sleep</span>
            <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#1E3A39' }}>{sleepHours}h</span>
          </div>
        )}
        {isSimulated && (
          <span style={{ fontSize: '0.65rem', color: '#B8D3CF', alignSelf: 'center', fontStyle: 'italic' }}>
            (connect Apple Watch for live data)
          </span>
        )}
      </div>
    );
  }

  return (
    <div style={{
      background: '#fff', borderRadius: '16px', padding: '20px',
      boxShadow: '0 2px 16px rgba(30,107,104,0.07)',
      border: '1px solid rgba(184,211,207,0.3)'
    }}>
      <div style={{
        fontSize: '0.68rem', color: '#8FA3A0', letterSpacing: '0.1em',
        textTransform: 'uppercase', fontWeight: '600', marginBottom: '14px'
      }}>
        Apple Health {isSimulated ? '· connect Apple Watch for live data' : ''}
      </div>

      <div style={{ display: 'flex', gap: '10px', marginBottom: hrv ? '14px' : 0 }}>
        {[
          { label: 'HRV',   value: hrv,         unit: 'ms'  },
          { label: 'Heart Rate', value: restingHR, unit: 'bpm' },
          { label: 'Sleep', value: sleepHours,   unit: 'h'   }
        ].filter(d => d.value).map(d => (
          <div key={d.label} style={{
            flex: 1, textAlign: 'center',
            background: 'rgba(247,243,236,0.8)', borderRadius: '10px', padding: '10px 8px'
          }}>
            <div style={{ fontSize: '0.62rem', color: '#AAB8B6', textTransform: 'uppercase', fontWeight: '600', letterSpacing: '0.06em', marginBottom: '2px' }}>
              {d.label}
            </div>
            <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1E3A39', fontFamily: "'Playfair Display', serif" }}>
              {d.value}<span style={{ fontSize: '0.68rem', color: '#8FA3A0' }}>{d.unit}</span>
            </div>
          </div>
        ))}
      </div>

      {hrvInterpretation && (
        <div style={{
          padding: '10px 12px', borderRadius: '10px',
          background: `${hrvInterpretation.color}10`,
          border: `1px solid ${hrvInterpretation.color}30`
        }}>
          <span style={{ fontSize: '0.78rem', color: hrvInterpretation.color, fontWeight: '600', marginRight: 6 }}>
            {hrvInterpretation.label}
          </span>
          <span style={{ fontSize: '0.78rem', color: '#8FA3A0' }}>{hrvInterpretation.tone}</span>
        </div>
      )}
    </div>
  );
}
